import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MaintenanceType, Segment } from '@core/interfaces';
import { chain, map } from 'lodash';

@Component({
  selector: 'tab-new-providers',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './tab-new-providers.component.html',
  styleUrls: ['./tab-new-providers.component.scss']
})
export class TabNewProvidersComponent implements OnInit {
  @Input()
  public serviceProvidersFormArray: FormArray;

  @Input()
  public loadingProviders = false;

  @Input()
  public isViewMode: boolean;

  public linkProviderForm: FormGroup;
  public segments: Partial<Segment>[];
  public duplicatedServiceProvider: Record<string, any>;
  public maintenanceTypesBySegment: Partial<MaintenanceType>[];

  constructor(private readonly formBuilder: FormBuilder) {
    this.duplicatedServiceProvider = null;
    this.linkProviderForm = this.formBuilder.group({
      segment: [null, [Validators.required]],
      maintenanceTypes: this.formBuilder.array([], [Validators.required])
    });
  }

  public ngOnInit(): void {
    this.serviceProvidersFormArray.markAllAsTouched();
  }

  public linkServiceProvider(): void {
    this.duplicatedServiceProvider = null;

    const linkProviderFormValue = this.linkProviderForm.getRawValue();
    linkProviderFormValue.maintenanceTypes = chain(linkProviderFormValue.maintenanceTypes)
      .filter(({ value }) => !value.isAllOption)
      .map('value')
      .value();

    this.validateServiceProviders(linkProviderFormValue);
    if (this.duplicatedServiceProvider) {
      this.linkProviderForm.setErrors({ duplicated: true });
      return;
    }

    const serviceProvidersControl = this.formBuilder.group({
      company: [null],
      deadlineContract: [null],
      maintenanceTypes: [linkProviderFormValue.maintenanceTypes],
      segment: [linkProviderFormValue.segment, [Validators.required]]
    });

    this.serviceProvidersFormArray.push(serviceProvidersControl);
    this.linkProviderForm.reset();
  }

  public deleteServiceProvider(index: number): void {
    const serviceProvider = this.serviceProvidersFormArray.at(index).value;
    const linkProviderForm = this.linkProviderForm.value;

    this.serviceProvidersFormArray.removeAt(index);

    const isSameSegment = linkProviderForm.segment && serviceProvider.segment.id === linkProviderForm.segment.id;
    if (isSameSegment && this.linkProviderForm.hasError('duplicated')) {
      this.linkProviderForm.setErrors({});
      this.linkProviderForm.updateValueAndValidity();
    }
  }

  public mappedMaintenanceTypes(maintenanceTypes: Partial<MaintenanceType>[]): string {
    return chain(maintenanceTypes).map('name').sortBy().value().join(', ');
  }

  private validateServiceProviders({ segment: segmentToAdd, maintenanceTypes }): void {
    const maintenanceTypesToAddId = map(maintenanceTypes, 'id');

    const existingServiceProviders = this.serviceProvidersFormArray.getRawValue();
    const existingServiceProvidersWithSameSegment = existingServiceProviders.filter((serviceProvider) => {
      return serviceProvider.segment.id === segmentToAdd.id;
    });

    if (!existingServiceProvidersWithSameSegment.length) {
      return;
    }

    const existingServiceProviderForAllMaintenanceTypes = existingServiceProvidersWithSameSegment
      .filter((serviceProvider) => !serviceProvider.maintenanceTypes.length);

    if (existingServiceProviderForAllMaintenanceTypes.length) {
      this.duplicatedServiceProvider = {
        allMaintenanceTypesAlreadyExists: true
      };

      return;
    }

    const existingServiceProvidersMaintenanceTypes = chain(existingServiceProvidersWithSameSegment)
      .flatMap('maintenanceTypes')
      .sortBy('name')
      .value();

    if (!maintenanceTypesToAddId.length) {
      this.duplicatedServiceProvider = {
        maintenanceTypes: existingServiceProvidersMaintenanceTypes
      };

      return;
    }


    const duplicatedServiceProvidersMaintenanceTypes = existingServiceProvidersMaintenanceTypes
      .filter(({ id }) => maintenanceTypesToAddId.includes(id));

    if (duplicatedServiceProvidersMaintenanceTypes.length) {
      this.duplicatedServiceProvider = {
        maintenanceTypes: duplicatedServiceProvidersMaintenanceTypes
      };
    }
  }
}
